import React, {useCallback, useContext, useEffect} from "react"
import {useTranslation} from "react-i18next"
import {useLocation, useNavigate} from "react-router-dom"

import Avatar from "@mui/material/Avatar"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CssBaseline from "@mui/material/CssBaseline"
import MuiDrawer from "@mui/material/Drawer"
import Grid from "@mui/material/Grid"
import {CSSObject, styled, Theme} from "@mui/material/styles"

import {useKeycloakLogout} from "@auth/hooks/useKeycloakLogout"

import {TenantUser} from "src/_generated/typed-document-nodes"
import bg from "src/assets/images/fondall.jpg"
import freedz from "src/assets/images/freedz.png"
import {ChaneRoleBoradCast, logOut} from "src/components/common/BoradCastChanel"
import DropDownMenu from "src/components/common/DropDownMenu"
import DropDownMenuLight from "src/components/common/DropDownMenuLight"
import {
    AccountAdminSmall,
    ApiIcon,
    CguIcon,
    ChangeRole,
    English,
    French,
    HelpCenterSmall,
    LogOutIcon,
    MyTicketIcon,
    UsageGuideIcon
} from "src/components/common/NdIcons/SvgCollection"
import {
    CGU_URL,
    COOKIES_URL,
    DOC_API_URL,
    LEGAL_TERMS_URL,
    SUPPORT_URL,
    USER_GUIDE_URL
} from "src/constants/external-urls"
import {localStorageItemName} from "src/constants/local-storage-item-name"
import ErrorContext from "src/context/ErrorContext"
import CommonUtils from "src/utils/Common"
import i18n from "src/utils/i18n"
import {readMainConfigValue} from "src/utils/mainConfig"
import {getAllNatures} from "src/utils/Queries/common"
import {initialTenantUser} from "src/utils/Queries/tenantUsers"
import Routing from "src/utils/Routing"
import {useApiFetch} from "src/utils/useApiFetch"

const drawerWidth = 240

const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
    }),
    overflowX: "hidden"
})

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`
    }
})

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open"
})(({theme, open}) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme)
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme)
    })
}))

const DrawerHeader: any = styled("div")(({theme}) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(0, 1),
    minHeight: "128px",
    margin: "10px 0",
    ...theme.mixins.toolbar
}))

function Sidebar() {
    const {sendApiRequest} = useApiFetch()
    const {t} = useTranslation("common")
    const context = useContext(ErrorContext)
    const {logout} = useKeycloakLogout()
    const [open] = React.useState(true)
    const [currentTenentUser, setCurrentTenentUser] = React.useState<TenantUser>(initialTenantUser)
    const location = useLocation()
    const navigate = useNavigate()

    useEffect(() => {
        const locacalUser: TenantUser = CommonUtils.GetLocal(localStorageItemName.tenantUser)
            ? JSON.parse(CommonUtils.GetLocal(localStorageItemName.tenantUser))
            : initialTenantUser
        if (currentTenentUser.email !== locacalUser.email) {
            setCurrentTenentUser(locacalUser)
        }
    }, [])
    const languageMenu = {
        button: (
            <>
                {CommonUtils.GetFullLanguage(CommonUtils.GetLocal(localStorageItemName.language)) === "Français" ? (
                    <French />
                ) : (
                    <English style={{width: 18, height: 12}} />
                )}
                <span className="normalCase" style={{fontSize: 10, marginLeft: 5}}>
                    {CommonUtils.GetFullLanguage(CommonUtils.GetLocal(localStorageItemName.language))}
                </span>
            </>
        ),
        items: [
            {
                key: "en",
                text: t("common:English"),
                click: () => changeLanguage("en")
            },
            {
                key: "fr",
                text: t("common:French"),
                click: () => changeLanguage("fr")
            }
        ]
    }

    const openNewTab = (url: string) => url && window.open(url, "_blank")

    const accountMenu: any = {
        topCard: (
            <Card className="generalTopCard accountTopCard">
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center"
                    }}
                >
                    <Avatar
                        style={{
                            background: "#F5FAFE",
                            color: "#307ADE",
                            width: 50,
                            height: 50,
                            fontSize: 20,
                            borderColor: "rgba(48, 123, 222, 0.1)",
                            borderWidth: 2,
                            borderStyle: "solid",
                            marginRight: 10
                        }}
                        className="poppinsLightFont"
                    >
                        {currentTenentUser.email.replace(".", "").substring(0, 2).toUpperCase()}
                    </Avatar>
                    <div style={{lineHeight: "20px"}}>
                        <span style={{fontSize: 14}}>{currentTenentUser.firstName}</span>
                        <br />
                        <span style={{color: "rgba(0, 0, 0, 0.7)", fontSize: 10}}>
                            {CommonUtils.GetLocal(localStorageItemName.selectedProduct) === "nd"
                                ? JSON.parse(CommonUtils.GetLocal(localStorageItemName.selectedRole))?.tenantName
                                : JSON.parse(CommonUtils.GetLocal(localStorageItemName.selectedRole))?.domainName}
                        </span>
                    </div>
                </div>
            </Card>
        ),
        bottomCard: (
            <Card className="generalTopCard accountBottomCard">
                <Grid
                    container
                    className="top7"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 8
                    }}
                >
                    <DropDownMenuLight {...languageMenu} />
                    <Button
                        className="logOutBtn"
                        onClick={() => {
                            context.setGlobalLoading(true)
                            logOut(() => {
                                logout()
                            })
                        }}
                    >
                        <LogOutIcon style={{marginRight: 5}} />
                        {t("common:Logout")}
                    </Button>
                </Grid>
                <Grid container className="accountLinks">
                    <Grid item xs={2}>
                        <a href={CGU_URL} target="_blank" rel="noreferrer">
                            {t("common:CGU")}
                        </a>
                    </Grid>
                    <Grid item xs={3}>
                        <a href={COOKIES_URL} target="_blank" rel="noreferrer">
                            {t("common:Cookies")}
                        </a>
                    </Grid>
                    <Grid item xs={4}>
                        <div style={{fontSize: 10}}>{`v${import.meta.env.VITE_BUILD_VERSION || __APP_VERSION__}`}</div>
                    </Grid>
                </Grid>
            </Card>
        ),
        button: (
            <div className="UserAccount">
                <Avatar
                    style={{
                        background: "#F5FAFE",
                        color: "#307ADE",
                        width: 50,
                        height: 50,
                        fontSize: 20,
                        borderColor: "rgba(48, 123, 222, 0.1)",
                        borderWidth: 2,
                        borderStyle: "solid"
                    }}
                    className="poppinsLightFont"
                >
                    {currentTenentUser.email.replace(".", "").substring(0, 2).toUpperCase()}
                </Avatar>

                <div className="UserInfo">
                    <div className="userInfoName">
                        {CommonUtils.GetLocal(localStorageItemName.selectedProduct) === "da"
                            ? JSON.parse(CommonUtils.GetLocal(localStorageItemName.selectedRole))?.domainName
                            : JSON.parse(CommonUtils.GetLocal(localStorageItemName.selectedRole))?.tenantName}
                    </div>
                    {CommonUtils.getDomain(
                        JSON.parse(CommonUtils.GetLocal(localStorageItemName.selectedRole))?.domainId
                    )?.parentDomainId &&
                        CommonUtils.GetLocal(localStorageItemName.selectedProduct) === "da" && (
                            <div style={{display: "flex", flex: 1}}>
                                <div className="userInfoDomainWrapper">
                                    <span className="userInfoDomain">
                                        {
                                            CommonUtils.getDomain(
                                                CommonUtils.getDomain(
                                                    JSON.parse(CommonUtils.GetLocal(localStorageItemName.selectedRole))
                                                        ?.domainId
                                                )?.parentDomainId
                                            )?.name
                                        }
                                    </span>
                                </div>
                            </div>
                        )}
                    {CommonUtils.GetLocal(localStorageItemName.selectedProduct) === "nd" && (
                        <div style={{display: "flex", flex: 1}}>
                            <div className="userInfoDomainWrapper">
                                <span className="userInfoDomain">
                                    {JSON.parse(CommonUtils.GetLocal(localStorageItemName.selectedRole))?.domainName}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        ),
        items: [
            {
                key: "HelpCenter",
                icon: <HelpCenterSmall />,
                subItem: [
                    {
                        key: "myTickets",
                        text: t("common:MyTickets"),
                        icon: <MyTicketIcon />,
                        click: () => openNewTab(readMainConfigValue("Link_CustomerSupport") ?? SUPPORT_URL)
                    },
                    {
                        key: "userGuide",
                        icon: <UsageGuideIcon />,
                        text: t("common:UserGuide"),
                        click: () => openNewTab(USER_GUIDE_URL)
                    },
                    {
                        key: "docApi",
                        icon: <ApiIcon />,
                        text: t("common:DocApi"),
                        click: () => openNewTab(DOC_API_URL)
                    },
                    {
                        key: "legalTerms",
                        icon: <CguIcon />,
                        text: t("common:LegalTerms"),
                        click: () => openNewTab(LEGAL_TERMS_URL)
                    }
                ],
                text: t("common:HelpCenter")
            }
        ]
    }

    const isAutoload = localStorage.getItem(localStorageItemName.isAutoLogin)
        ? localStorage.getItem(localStorageItemName.isAutoLogin)
        : "false"

    if (JSON.stringify(isAutoload) === '"false"') {
        accountMenu.items.push({
            key: "changeRole",
            icon: <ChangeRole />,
            text: t("common:ChangeRole"),
            click: () => changeRole(),
            subItem: undefined
        })
    }

    if (CommonUtils.GetLocal("selectedProduct") === "nd") {
        accountMenu.items.splice(1, 0, {
            key: "accountSettings",
            icon: <AccountAdminSmall />,
            text: t("common:AccountSettings"),
            subItem: undefined,
            click: () => navigate(Routing.GetUrl("Administration").replace(":selectedTab", "0"))
        })
    }

    function FreedzLogo() {
        return <img alt="freedz" src={freedz} style={{width: 21, height: 21}} />
    }

    if (!["preprod-app.docoon.com", "app.docoon.com"].includes(window.location.host)) {
        accountMenu.items.push({
            key: "changeRole",
            icon: <FreedzLogo />,
            text: t("common:freedz"),
            click: () => (window.location.href = "https://portal.docoon.com/#/auth"),
            subItem: undefined
        })
    }

    function changeRole() {
        ChaneRoleBoradCast(() => {
            localStorage.setItem(localStorageItemName.isRoleSelected, "false")
            localStorage.setItem(localStorageItemName.selectedProduct, "")
            window.location.href = "/"
        })
    }

    async function changeLanguage(lng: string) {
        localStorage.setItem(localStorageItemName.language, lng)
        i18n.changeLanguage(lng)
        try {
            const naturesList: any = await sendApiRequest({
                url: CommonUtils.GetSetting("BUSINESS_API_URL"),
                query: getAllNatures,
                variables: {
                    params: {
                        sortField: "LABEL",
                        sortDirection: "ASCENDING"
                    },
                    after: "",
                    language: lng
                },
                odmToken: JSON.parse(CommonUtils.GetLocal(localStorageItemName.selectedRole))?.token,
                type: "query",
                cache: context.cache,
                restore: true
            })
            localStorage.setItem(
                localStorageItemName.listNatures,
                JSON.stringify(naturesList.data.natureList.edges.filter((item) => item.code !== "ChorusB2G"))
            )
            window.location.reload()
        } catch (error) {
            console.error("Failed to change language:", error)
        }
    }

    const isRole = React.useMemo(
        () => CommonUtils.ToBoolean(CommonUtils.GetLocal(localStorageItemName.isRoleSelected)),
        []
    )

    const dynamicMenu = useCallback(
        (isRole2: boolean) => {
            return isRole ? Routing.CreateMenu(isRole2) : null
        },
        [isRole]
    )

    return (
        <>
            {!Routing.IsOffline(location.pathname) && isRole && (
                <Box sx={{display: "flex"}}>
                    <CssBaseline />
                    <Drawer
                        variant="permanent"
                        className="sideMenuMenu"
                        open={open}
                        sx={{
                            display: {xs: "none", sm: "block"},
                            "& .MuiDrawer-paper": {
                                backgroundImage: `url(${bg})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center center"
                            }
                        }}
                    >
                        <DrawerHeader style={{margin: "10px 20px", padding: 0}}>
                            <Grid
                                style={{cursor: "pointer"}}
                                className="FullParent"
                                onClick={() => (window.location.href = "/")}
                            >
                                <img src="/logo.png" alt="logo" style={{width: "100%", margin: 0}} />
                            </Grid>
                        </DrawerHeader>
                        {dynamicMenu(isRole)}
                        {isRole && (
                            <Grid className="accountIcon">
                                <DropDownMenu {...accountMenu} />
                            </Grid>
                        )}
                    </Drawer>
                </Box>
            )}
        </>
    )
}

export default Sidebar
